import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants/listPagination';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { formatDate, presentPhone } from '../../helpers/formatters';
import { applyName } from '../../helpers/captureMode';
import { formatCalendarDate, isVisitManagedPartnerModality, localISODate, partnerModalityLabel, partnerStatusLabel } from './partnerLabels';

function VisitRow({ visit }) {
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} noWrap>{applyName(visit.partner_name)}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {[visit.partner_address, visit.partner_city].filter(Boolean).join(' · ') || 'Sem endereço'}
                </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexShrink={0}>
                <Button size="small" component={RouterLink} to={`/admin/partners/${visit.partner_uuid}`}>
                    Detalhes
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    component={RouterLink}
                    to={`/admin/partners/visits/${visit.uuid}`}
                    startIcon={<EventIcon fontSize="small" />}
                >
                    + Visita
                </Button>
            </Stack>
        </Stack>
    );
}

function VisitsDayCard({ title, count, visits, emptyHint }) {
    const list = visits || [];
    const hasVisits = list.length > 0;

    return (
        <Card sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: hasVisits ? 1 : 0 }}>
                    <Typography color="text.secondary">{title}</Typography>
                    <Typography variant="h4" fontWeight={700} lineHeight={1}>
                        {count ?? list.length}
                    </Typography>
                </Stack>
                {hasVisits ? (
                    <Box>
                        {emptyHint ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {emptyHint}
                            </Typography>
                        ) : null}
                        {list.map((visit) => (
                            <VisitRow key={visit.uuid} visit={visit} />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Nenhuma visita agendada.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function PartnersDashboardPage() {
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [data, setData] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [q, setQ] = useState('');
    const [modality, setModality] = useState('');
    const [status, setStatus] = useState('');
    const [visitByPartner, setVisitByPartner] = useState({});
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!enabled) return undefined;
        setLoading(true);
        try {
            const from = localISODate();
            const horizon = new Date();
            horizon.setDate(horizon.getDate() + 30);
            const [dashboardResponse, partnersResponse, calendarResponse] = await Promise.all([
                api.get('/commerce-partners/dashboard'),
                api.get('/commerce-partners', { params: { page, limit, q, modality, status } }),
                api.get('/commerce-partners/calendar', { params: { from, to: localISODate(horizon) } }),
            ]);
            setData(dashboardResponse?.data?.data || null);
            setItems(partnersResponse?.data?.data || []);
            setTotal(partnersResponse?.data?.total || 0);

            const nextVisits = {};
            (calendarResponse?.data?.data || []).forEach((visit) => {
                if (visit.status === 'done' || !visit.partner_uuid) return;
                if (!nextVisits[visit.partner_uuid]) {
                    nextVisits[visit.partner_uuid] = visit;
                }
            });
            setVisitByPartner(nextVisits);
        } catch {
            setData(null);
            setItems([]);
            setTotal(0);
            setVisitByPartner({});
        } finally {
            setLoading(false);
        }
        return undefined;
    }, [enabled, page, limit, q, modality, status]);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = async (partner) => {
        const result = await Swal.fire({
            title: 'Excluir parceria?',
            text: partner?.name ? `${applyName(partner.name)} será removida.` : undefined,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Excluir',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) return;
        try {
            await api.delete(`/commerce-partners/${partner.uuid}`);
            await load();
            Swal.fire('OK', 'Parceria excluída.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao excluir.', 'error');
        }
    };

    if (flagLoading || loading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;

    const totalPages = Math.max(1, Math.ceil(total / limit));

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Typography color="text.primary">Parcerias</Typography>
            </Breadcrumbs>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
                <Typography variant="h5">Parcerias</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button component={RouterLink} to="/admin/partners/calendar" variant="outlined">Calendário</Button>
                    <Button component={RouterLink} to="/admin/partners/map" variant="outlined">Mapa</Button>
                    <QuotaLimitedButton quotaKey="commerce_partners" to="/admin/partners/new" variant="contained">
                        Nova parceria
                    </QuotaLimitedButton>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                <VisitsDayCard
                    title="Visitas hoje"
                    count={data?.today_count}
                    visits={data?.today_visits}
                    emptyHint="Informe o que foi vendido e o que será reposto em cada visita."
                />
                <VisitsDayCard
                    title="Visitas amanhã"
                    count={data?.tomorrow_count}
                    visits={data?.tomorrow_visits}
                />
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        label="Buscar"
                        placeholder="Nome do estabelecimento ou responsável"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        fullWidth
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Modalidade</InputLabel>
                        <Select label="Modalidade" value={modality} onChange={(e) => setModality(e.target.value)}>
                            <MenuItem value="">Todas</MenuItem>
                            <MenuItem value="pay_to_sell">Pay to sell</MenuItem>
                            <MenuItem value="consignment">Consignação</MenuItem>
                            <MenuItem value="repasse">Repasse (Comissão)</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="active">Ativa</MenuItem>
                            <MenuItem value="paused">Pausada</MenuItem>
                            <MenuItem value="ended">Encerrada</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" onClick={() => { setPage(1); load(); }}>Buscar</Button>
                </Stack>
            </Paper>

            <TableContainer component={Paper}>
                <Table sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '26%' }}>Estabelecimento</TableCell>
                            <TableCell sx={{ width: '15%' }}>Modalidade</TableCell>
                            <TableCell sx={{ width: '18%' }}>Produtos</TableCell>
                            <TableCell sx={{ width: '10%' }}>Status</TableCell>
                            <TableCell sx={{ width: '10%' }}>Próxima visita</TableCell>
                            <TableCell align="right" sx={{ width: '21%' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const openVisit = visitByPartner[item.uuid];
                            const productNames = (item.items || [])
                                .map((product) => product.product_name)
                                .filter(Boolean);
                            return (
                                <TableRow key={item.uuid} hover>
                                    <TableCell>
                                        <Typography
                                            component={RouterLink}
                                            to={`/admin/partners/${item.uuid}`}
                                            fontWeight={600}
                                            sx={{
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}
                                        >
                                            {applyName(item.name)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {[applyName(item.contact_name), presentPhone(item.contact_phone), item.city].filter(Boolean).join(' · ') || 'Sem responsável/telefone'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{partnerModalityLabel(item.modality)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" noWrap title={productNames.join(', ')}>
                                            {productNames.length > 0 ? productNames.join(', ') : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5} alignItems="flex-start">
                                            <Chip size="small" label={partnerStatusLabel(item.status)} />
                                            <Typography variant="caption" color="text.secondary">
                                                Desde {item.created_at ? formatDate(item.created_at, false) : '—'}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{openVisit?.planned_on ? formatCalendarDate(openVisit.planned_on) : '—'}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                                            {openVisit && isVisitManagedPartnerModality(item.modality) ? (
                                                <Tooltip title="Informar visita">
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        component={RouterLink}
                                                        to={`/admin/partners/visits/${openVisit.uuid}`}
                                                        startIcon={<EventIcon fontSize="small" />}
                                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                                    >
                                                        + Visita
                                                    </Button>
                                                </Tooltip>
                                            ) : null}
                                            <Tooltip title="Editar">
                                                <IconButton size="small" component={RouterLink} to={`/admin/partners/${item.uuid}/edit`}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>Nenhuma parceria encontrada.</TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
            <CustomPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </Box>
    );
}

export default PartnersDashboardPage;
